import { Alert, Box, Button, Stack } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import { useEffect, useMemo, useState } from 'react';
import { FeedbackSnackbar, ResourcePage, ResourceCollection, StatusBadge, type ResourceCollectionState } from '../../../design-system';
import { emptyCommonDraft } from './ReferenceCatalogFields';
import { BackToReferenceData, canEditCatalogItem, catalogActionsColumn, useReferenceLifecycle } from './ReferenceLifecycle';
import { WorkSpaceDetailDialog } from './WorkSpaceDetailDialog';
import { ReferenceBulkCreateDialog } from './ReferenceBulkCreateDialog';
import { referenceCreateActions } from './ReferenceCreateActions';
import {
  WorkSpaceEditorDialog,
  workSpaceDraftValid,
  type WorkSpaceDraft,
  type WorkSpaceEditor,
} from './WorkSpaceEditorDialog';
import {
  apiErrorMessage,
  filterCatalog,
  fixtureReferenceCode,
  replaceCatalogItem,
  replaceCatalogItems,
  sortCatalog,
  statusLabel,
  statusOptions,
  statusTone,
  todayInSeoul,
  useOperationKey,
  type ReferenceDataResource,
  type ReferenceSettingsCommonProps,
} from '../model/referenceData';
import { createReferenceBulkPort, referenceBulkPayloads, referenceBulkWorkflows } from '../model/referenceBulk';
import type {
  ReferenceLocation,
  ReferenceWorkSpace,
  ReferenceWorkSpaceChangeInput,
  ReferenceWorkSpaceCreateInput,
} from '../model/types';
export function WorkSpaceReferenceSettings({
  port,
  fixtureMode,
  canRead,
  canWrite,
  onBack,
  titleRef,
  locationResource,
  workSpaceResource,
}: ReferenceSettingsCommonProps & {
  locationResource: ReferenceDataResource<ReferenceLocation>;
  workSpaceResource: ReferenceDataResource<ReferenceWorkSpace>;
}) {
  const locations = useMemo(() => sortCatalog(locationResource.data ?? []), [locationResource.data]);
  const activeLocations = useMemo(() => locations.filter((location) => location.status === 'ACTIVE'), [locations]);
  const [items, setItems] = useState<ReferenceWorkSpace[]>(workSpaceResource.data ?? []);
  const [selected, setSelected] = useState<ReferenceWorkSpace>();
  const [editor, setEditor] = useState<WorkSpaceEditor>();
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [busy, setBusy] = useState(false);
  const [editorError, setEditorError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const [bulkWarning, setBulkWarning] = useState<string>();
  const [bulkOpen, setBulkOpen] = useState(false);
  const operationKey = useOperationKey();
  useEffect(() => setItems(sortCatalog(workSpaceResource.data ?? [])), [workSpaceResource.data]);
  const filteredItems = useMemo(
    () => filterCatalog(items, query, status).filter((item) => !locationFilter || item.location.id === locationFilter),
    [items, query, status, locationFilter],
  );
  const bulkWorkflow = useMemo(() => referenceBulkWorkflows.workSpaces(locations), [locations]);
  const bulkReferencePort = useMemo(() => createReferenceBulkPort('locationCode', locations), [locations]);
  const locationFilterOptions = useMemo(
    () => [
      {
        label: '전체',
        value: '',
      },
      ...locations.map((location) => ({
        label: location.descriptor,
        value: location.id,
      })),
    ],
    [locations],
  );
  const editorLocationOptions = useMemo(() => {
    const options = activeLocations.map((location) => ({
      id: location.id,
      code: location.code,
      descriptor: location.descriptor,
    }));
    const current = editor?.item?.location;
    if (current && !options.some((location) => location.id === current.id)) options.push(current);
    return options;
  }, [activeLocations, editor?.item?.location]);
  const columns = useMemo<GridColDef<ReferenceWorkSpace>[]>(
    () => [
      {
        field: 'descriptor',
        headerName: '업무 공간',
        flex: 1,
        minWidth: 190,
      },
      {
        field: 'code',
        headerName: '참조 코드',
        width: 180,
      },
      {
        field: 'location',
        headerName: '근무지',
        width: 170,
        valueGetter: (_value, row) => row.location.descriptor,
      },
      {
        field: 'status',
        headerName: '상태',
        width: 140,
        renderCell: ({ row }) => <StatusBadge label={statusLabel(row.status)} tone={statusTone(row.status)} />,
      },
    ],
    [],
  );
  const updateItem = (item: ReferenceWorkSpace) => {
    setItems((current) => replaceCatalogItem(current, item));
    setSelected((current) => (current?.id === item.id ? item : current));
    if (!fixtureMode) workSpaceResource.reload();
  };
  const lifecycle = useReferenceLifecycle<ReferenceWorkSpace>({
    kindLabel: '업무 공간',
    fixtureMode,
    change: (id, input, key) => port.changeReferenceWorkSpace(id, input as ReferenceWorkSpaceChangeInput, key),
    onChanged: updateItem,
    onNotice: setNotice,
  });
  const openCreate = () => {
    if (!activeLocations.length) return;
    setEditor({
      mode: 'create',
      draft: {
        ...emptyCommonDraft(),
        locationId: activeLocations[0].id,
      },
    });
    setEditorError(undefined);
    operationKey.reset();
  };
  const openEdit = (item: ReferenceWorkSpace) => {
    setSelected(undefined);
    setEditor({
      mode: 'edit',
      item,
      draft: {
        descriptor: item.descriptor,
        locationId: item.location.id,
        effectiveDate: item.status === 'SCHEDULED' ? item.effectiveFrom : todayInSeoul(),
        reason: '',
      },
    });
    setEditorError(undefined);
    operationKey.reset();
  };
  const changeDraft = <K extends keyof WorkSpaceDraft>(field: K, value: WorkSpaceDraft[K]) => {
    setEditor((current) =>
      current
        ? {
            ...current,
            draft: {
              ...current.draft,
              [field]: value,
            },
          }
        : current,
    );
    setEditorError(undefined);
  };
  const save = () => {
    if (!editor || !workSpaceDraftValid(editor.draft, editor.mode) || busy) return;
    const location =
      locations.find((candidate) => candidate.id === editor.draft.locationId) ??
      (editor.item?.location.id === editor.draft.locationId ? editor.item.location : undefined);
    if (!location) {
      setEditorError('선택한 근무지를 찾을 수 없습니다. 근무지 목록을 다시 확인해 주세요.');
      return;
    }
    setBusy(true);
    setEditorError(undefined);
    let request: Promise<ReferenceWorkSpace>;
    if (editor.mode === 'create') {
      const payload: ReferenceWorkSpaceCreateInput = {
        descriptor: editor.draft.descriptor.trim(),
        location: {
          id: location.id,
        },
        effectiveDate: editor.draft.effectiveDate,
        reason: editor.draft.reason.trim(),
      };
      const key = operationKey.forPayload(payload);
      request = fixtureMode
        ? Promise.resolve({
            id: `work-space-fixture-${crypto.randomUUID()}`,
            code: fixtureReferenceCode(),
            descriptor: payload.descriptor,
            location: {
              id: location.id,
              code: location.code,
              descriptor: location.descriptor,
            },
            status: payload.effectiveDate > todayInSeoul() ? 'SCHEDULED' : 'ACTIVE',
            effectiveFrom: payload.effectiveDate,
            version: 1,
          })
        : port.createReferenceWorkSpace(payload, key);
    } else {
      const payload: ReferenceWorkSpaceChangeInput = {
        version: editor.item!.version,
        descriptor: editor.draft.descriptor.trim(),
        location: {
          id: location.id,
        },
        effectiveDate: editor.draft.effectiveDate,
        reason: editor.draft.reason.trim(),
      };
      const key = operationKey.forPayload(payload);
      request = fixtureMode
        ? Promise.resolve({
            ...editor.item!,
            descriptor: payload.descriptor ?? editor.item!.descriptor,
            location: {
              id: location.id,
              code: location.code,
              descriptor: location.descriptor,
            },
            effectiveFrom: payload.effectiveDate,
            version: editor.item!.version + 1,
          })
        : port.changeReferenceWorkSpace(editor.item!.id, payload, key);
    }
    const savedMode = editor.mode;
    void request
      .then(
        (saved) => {
          updateItem(saved);
          setEditor(undefined);
          setNotice(`${saved.descriptor} 업무 공간을 ${savedMode === 'create' ? '등록' : '변경'}했습니다.`);
          operationKey.reset();
        },
        (cause: unknown) => setEditorError(apiErrorMessage(cause, '업무 공간 정보를 저장하지 못했습니다.')),
      )
      .finally(() => setBusy(false));
  };
  const collectionState: ResourceCollectionState = workSpaceResource.loading
    ? {
        kind: 'loading',
        label: '업무 공간 목록을 불러오는 중입니다.',
      }
    : workSpaceResource.error
      ? {
          kind: 'error',
          message: workSpaceResource.error,
          onRetry: workSpaceResource.reload,
          retryLabel: '업무 공간 목록 다시 시도',
        }
      : items.length === 0
        ? {
            kind: 'empty',
            title: '등록된 업무 공간이 없습니다.',
            description: canWrite ? '업무 공간 등록을 눌러 첫 항목을 추가해 주세요.' : '등록된 업무 공간이 없습니다.',
          }
        : {
            kind: 'ready',
          };
  const createDisabled = locationResource.loading || Boolean(locationResource.error) || !activeLocations.length;
  const createDisabledReason = locationResource.loading
    ? undefined
    : locationResource.error
      ? '근무지 목록을 불러온 뒤 업무 공간을 등록할 수 있습니다.'
      : '먼저 사용 중인 근무지를 등록해 주세요.';
  return (
    <>
      <FeedbackSnackbar open={Boolean(notice)} message={notice} tone="success" onClose={() => setNotice(undefined)} />
      <ResourcePage
        title="업무 공간"
        titleRef={titleRef}
        width="standard"
        navigation={<BackToReferenceData onBack={onBack} />}
        {...(canWrite ? referenceCreateActions({
          createLabel: "업무 공간 등록",
          onCreate: openCreate,
          onBulkCreate: () => setBulkOpen(true),
          disabled: createDisabled,
          loading: locationResource.loading,
          disabledReason: createDisabledReason,
        }) : {})}
      >
        <Box
          sx={{
            height: '100%',
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {bulkWarning && <Alert severity="warning" onClose={() => setBulkWarning(undefined)} sx={{ mb: 1.5, flexShrink: 0 }}>{bulkWarning}</Alert>}
          {!canRead || locationResource.error ? (
            <Stack
              spacing={1.5}
              sx={{
                mb: canRead ? 1.5 : 0,
                flexShrink: 0,
              }}
            >
              {!canRead ? (
                <Alert severity="warning">업무 공간 기준정보를 조회하려면 staffing.read 권한이 필요합니다.</Alert>
              ) : null}
              {locationResource.error ? (
                <Alert
                  severity="error"
                  action={
                    <Button color="inherit" size="small" onClick={locationResource.reload}>
                      다시 시도
                    </Button>
                  }
                >
                  근무지 목록을 불러오지 못해 업무 공간 등록과 근무지 필터를 사용할 수 없습니다.
                </Alert>
              ) : null}
            </Stack>
          ) : null}
          {canRead ? (
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
              }}
            >
              <ResourceCollection
                state={collectionState}
                rows={filteredItems}
                totalCount={items.length}
                columns={[...columns, catalogActionsColumn({ canWrite, onInspect: setSelected, onEdit: openEdit, onLifecycle: lifecycle.open })]}
                query={query}
                onQueryChange={setQuery}
                searchPlaceholder="업무 공간 이름 또는 코드 검색"
                rowDisplayMode="all"
                showTotalCount={false}
                filters={[
                  {
                    label: '근무지',
                    value: locationFilter,
                    onChange: setLocationFilter,
                    options: locationFilterOptions,
                  },
                  {
                    label: '상태',
                    value: status,
                    onChange: setStatus,
                    options: statusOptions,
                  },
                ]}
                onRowClick={({ row }) => canWrite && canEditCatalogItem(row) ? openEdit(row) : setSelected(row)}
              />
            </Box>
          ) : null}
        </Box>
      </ResourcePage>

      <WorkSpaceDetailDialog
        workSpace={selected}
        canWrite={canWrite}
        onClose={() => setSelected(undefined)}
        onEdit={() => selected && openEdit(selected)}
        onLifecycle={(action) => selected && lifecycle.open(action, selected)}
      />

      <WorkSpaceEditorDialog
        editor={editor}
        busy={busy}
        editorError={editorError}
        locations={editorLocationOptions}
        onClose={() => {
          if (!busy) setEditor(undefined);
        }}
        onSubmit={save}
        onChange={changeDraft}
      />
      <ReferenceBulkCreateDialog<ReferenceWorkSpace>
        open={bulkOpen}
        workflow={bulkWorkflow}
        referencePort={bulkReferencePort}
        onClose={() => setBulkOpen(false)}
        create={(row, key) => {
          const payload = referenceBulkPayloads.workSpace(row, locations);
          const location = locations.find((candidate) => candidate.id === payload.location.id);
          if (!location) return Promise.reject(new Error('사용 중인 근무지를 찾을 수 없습니다.'));
          return fixtureMode
            ? Promise.resolve({
                id: `work-space-fixture-${crypto.randomUUID()}`,
                code: fixtureReferenceCode(),
                descriptor: payload.descriptor,
                location: { id: location.id, code: location.code, descriptor: location.descriptor },
                status: payload.effectiveDate > todayInSeoul() ? 'SCHEDULED' as const : 'ACTIVE' as const,
                effectiveFrom: payload.effectiveDate,
                version: 1,
              })
            : port.createReferenceWorkSpace(payload, key);
        }}
        onCreated={(created) => {
          setItems((current) => replaceCatalogItems(current, created));
          if (!fixtureMode) workSpaceResource.reload();
        }}
        onApplied={({ succeeded, failed }) => {
          setBulkWarning(failed ? `업무 공간 ${succeeded.toLocaleString()}개를 등록했고 ${failed.toLocaleString()}개는 실패했습니다.` : undefined);
          setNotice(failed ? undefined : `업무 공간 ${succeeded.toLocaleString()}개를 등록했습니다.`);
        }}
      />
      {lifecycle.dialog}
    </>
  );
}
